import { customInstance } from "@repo/api-client";

/**
 * Client contract for the "Connect Google Business" surface (`/api/v1/google-business`,
 * tasks/google-business-connect). Account-level (not venue-scoped): one Google account per tenant.
 * Goes through `@repo/api-client`'s fetch mutator (bearer token + typed `ApiError`), mirroring
 * `placeMatchApi`. No Google token is ever returned here — only connection status + mapping metadata.
 */

export interface GbpLocation {
  locationId: string;
  title: string;
  placeId?: string | null;
  /** The venue this location is mapped to, or null when unmapped. */
  venueId?: string | null;
  version: number;
}

export interface GbpConnectionStatus {
  connected: boolean;
  googleAccountId?: string | null;
  connectedAt?: string | null;
  locations: GbpLocation[];
}

export interface GbpReview {
  reviewId: string;
  reviewerName: string;
  /** 1..5. */
  starRating: number;
  comment?: string | null;
  createTime?: string | null;
  replyComment?: string | null;
  answered: boolean;
}

export interface GbpReviews {
  averageRating?: number | null;
  totalReviewCount: number;
  reviews: GbpReview[];
}

export interface GbpRecommendation {
  /** ADD_WEBSITE | ADD_PHONE | ADD_HOURS | ANSWER_REVIEWS | LOW_RATING. */
  kind: string;
  /** INFO | SUGGESTION | WARNING. */
  severity: string;
  count?: number | null;
}

export interface GbpPhoneSync {
  ourPhone?: string | null;
  googlePhone?: string | null;
  inSync: boolean;
}

const base = "/api/v1/google-business";

export const googleBusinessApi = {
  getStatus: () => customInstance<GbpConnectionStatus>(`${base}/status`, { method: "GET" }),

  /** Returns the Google consent URL to send the browser to (account root). */
  connect: () => customInstance<{ consentUrl: string }>(`${base}/connect`, { method: "POST" }),

  /** Re-list the account's GBP locations from Google and auto-map by place id. Returns the mapped count. */
  sync: () => customInstance<{ mappedCount: number }>(`${base}/sync`, { method: "POST" }),

  disconnect: () => customInstance<void>(`${base}/disconnect`, { method: "POST" }),

  /** Map (venueId set) or clear (venueId null) a location's venue. CAS on version; a 409 surfaces as ApiError. */
  mapLocation: (locationId: string, venueId: string | null, version: number) =>
    customInstance<GbpLocation>(`${base}/locations/${encodeURIComponent(locationId)}/mapping`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ venueId, version }),
    }),

  reviews: (locationId: string) =>
    customInstance<GbpReviews>(`${base}/locations/${encodeURIComponent(locationId)}/reviews`, {
      method: "GET",
    }),

  recommendations: (locationId: string) =>
    customInstance<GbpRecommendation[]>(
      `${base}/locations/${encodeURIComponent(locationId)}/recommendations`,
      { method: "GET" },
    ),

  replyToReview: (locationId: string, reviewId: string, comment: string) =>
    customInstance<void>(
      `${base}/locations/${encodeURIComponent(locationId)}/reviews/${encodeURIComponent(reviewId)}/reply`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ comment }),
      },
    ),

  deleteReviewReply: (locationId: string, reviewId: string) =>
    customInstance<void>(
      `${base}/locations/${encodeURIComponent(locationId)}/reviews/${encodeURIComponent(reviewId)}/reply`,
      { method: "DELETE" },
    ),

  phoneSync: (locationId: string) =>
    customInstance<GbpPhoneSync>(`${base}/locations/${encodeURIComponent(locationId)}/phone-sync`, {
      method: "GET",
    }),

  pushPhone: (locationId: string) =>
    customInstance<void>(`${base}/locations/${encodeURIComponent(locationId)}/phone-sync`, {
      method: "POST",
    }),
};

export const googleBusinessKeys = {
  status: () => ["google-business", "status"] as const,
  reviews: (locationId: string) => ["google-business", "reviews", locationId] as const,
  recommendations: (locationId: string) =>
    ["google-business", "recommendations", locationId] as const,
  phoneSync: (locationId: string) => ["google-business", "phone-sync", locationId] as const,
};
